/**
 * Yönetici Sınav Oluştur — Spec Bölüm 7.1 + S2.T6
 *
 * Akış:
 *   1) /yonetici/sinavlar/yeni
 *   2) GET /api/manager/templates — sektör şablonu kartlarını listele
 *   3) Yönetici şablon seçer, anonim mod toggle eder (Spec 7.3.3)
 *   4) POST /api/manager/exams { templateId, anonymousMode }
 *   5) Backend stratifiye soru seçer, accessCode üretir (FIRMA-XXXX)
 *   6) Redirect: /yonetici/sinavlar/:id (sınav detay + davet linki)
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Header } from '../components/Header.js';
import {
  Alert,
  Button,
  Card,
  Checkbox,
} from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/auth-client.js';

interface Template {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  durationMin: number;
  questionCount: number;
  exampleIndustries: string[];
  emphasizedSubtopics: string[];
  topicWeights: Record<string, number>;
  difficultyDistribution: Record<string, number>;
}

interface CreatedExam {
  id: string;
  accessCode: string;
  durationMin: number;
  questionCount: number;
  anonymousMode: boolean;
}

const TOPIC_LABELS: Record<string, string> = {
  PM: 'Parça Modelleme',
  AS: 'Assembly',
  DR: 'Drawing',
  SK: 'Sketch & Tasarım Niyeti',
  SM: 'Sac Metal',
  WD: 'Weldments',
  SF: 'Surfacing',
  CF: 'Configurations',
  GN: 'Genel SW',
  DT: 'Tasarım Teorisi',
};

export function ExamCreatePage(): JSX.Element {
  const [, setLocation] = useLocation();
  const { data: session, isPending: sessionPending } = useSession();
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [anonymousMode, setAnonymousMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionPending) return;
    if (!session?.user) {
      setLocation('/yonetici/giris');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<{ templates: Template[] }>(
          '/api/manager/templates',
        );
        if (cancelled) return;
        setTemplates(data.templates);
        if (data.templates[0]) setSelectedId(data.templates[0].id);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Şablonlar yüklenemedi');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, sessionPending, setLocation]);

  const selectedTemplate =
    templates?.find((t) => t.id === selectedId) ?? null;

  async function handleCreate(): Promise<void> {
    if (!selectedId) {
      setError('Lütfen bir şablon seçin');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const exam = await api.post<CreatedExam>('/api/manager/exams', {
        templateId: selectedId,
        anonymousMode,
      });
      setLocation(`/yonetici/sinavlar/${exam.id}`);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError('Sınav oluşturulurken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  }

  if (sessionPending || loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-4xl px-6 py-12">
          <p className="text-slate-500">Yükleniyor…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-4xl px-6 py-12">
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setLocation('/yonetici/dashboard')}
            className="text-sm font-medium text-blue-900 hover:underline"
          >
            ← Panel
          </button>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">
            Yeni Sınav Oluştur
          </h1>
          <p className="mt-1 text-slate-600">
            Sektörünüze en uygun şablonu seçin. Sorular havuzdan otomatik
            stratifiye edilecek.
          </p>
        </div>

        {error && (
          <div className="mb-6">
            <Alert variant="danger">{error}</Alert>
          </div>
        )}

        {/* Şablon kartları */}
        <div className="grid gap-4 sm:grid-cols-2">
          {templates?.map((t) => {
            const isSelected = t.id === selectedId;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedId(t.id)}
                className={`text-left transition-all ${
                  isSelected
                    ? 'rounded-xl border-2 border-blue-900 bg-blue-50 p-5 shadow-md'
                    : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm hover:border-blue-400 hover:shadow-md'
                }`}
              >
                <div className="flex items-start justify-between">
                  <h3 className="text-lg font-semibold text-slate-900">
                    {t.name}
                  </h3>
                  {isSelected && (
                    <span className="rounded-full bg-blue-900 px-2 py-0.5 text-xs font-medium text-white">
                      Seçili
                    </span>
                  )}
                </div>
                {t.description && (
                  <p className="mt-1 text-sm text-slate-600">{t.description}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                    {t.questionCount} soru
                  </span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                    {t.durationMin} dk
                  </span>
                </div>
                {t.exampleIndustries.length > 0 && (
                  <p className="mt-2 text-xs text-slate-500">
                    Örnek: {t.exampleIndustries.slice(0, 3).join(', ')}
                  </p>
                )}
              </button>
            );
          })}
        </div>

        {/* Konu ağırlıkları breakdown */}
        {selectedTemplate && (
          <Card className="mt-6">
            <h3 className="text-base font-semibold text-slate-900">
              Konu Dağılımı — {selectedTemplate.name}
            </h3>
            <div className="mt-4 space-y-2">
              {Object.entries(selectedTemplate.topicWeights)
                .sort(([, a], [, b]) => b - a)
                .filter(([, w]) => w > 0)
                .map(([topic, weight]) => (
                  <div key={topic} className="flex items-center gap-3">
                    <span className="w-40 text-sm text-slate-700">
                      {TOPIC_LABELS[topic] ?? topic}
                    </span>
                    <div className="h-2 flex-1 rounded-full bg-slate-100">
                      <div
                        className="h-full rounded-full bg-blue-900"
                        style={{ width: `${weight * 100}%` }}
                      />
                    </div>
                    <span className="w-12 text-right text-sm font-medium text-slate-700">
                      {Math.round(weight * 100)}%
                    </span>
                  </div>
                ))}
            </div>

            <div className="mt-4 border-t border-slate-200 pt-4">
              <h4 className="text-sm font-semibold text-slate-900">
                Zorluk Dağılımı
              </h4>
              <div className="mt-2 flex gap-2 text-xs">
                {Object.entries(selectedTemplate.difficultyDistribution).map(
                  ([diff, weight]) => (
                    <span
                      key={diff}
                      className="rounded-full bg-slate-100 px-2 py-1 text-slate-700"
                    >
                      {diff}: {Math.round(weight * 100)}%
                    </span>
                  ),
                )}
              </div>
            </div>

            {selectedTemplate.emphasizedSubtopics.length > 0 && (
              <div className="mt-4 border-t border-slate-200 pt-4">
                <h4 className="text-sm font-semibold text-slate-900">
                  Vurgulanan Alt Konular
                </h4>
                <div className="mt-2 flex flex-wrap gap-2">
                  {selectedTemplate.emphasizedSubtopics.map((s) => (
                    <span
                      key={s}
                      className="rounded-full bg-amber-50 px-2 py-1 text-xs text-amber-900"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Anonim mod */}
        <Card className="mt-6">
          <Checkbox
            id="anonymousMode"
            label={
              <span>
                <strong>Anonim Mod</strong> — Adaylar ad/e-posta yerine takma ad
                (örn. ADAY-001) ile katılır. KVKK risk azaltma için tercih
                edilebilir (Spec 7.3.3).
              </span>
            }
            checked={anonymousMode}
            onChange={(e) => setAnonymousMode(e.target.checked)}
          />
        </Card>

        <div className="mt-8 flex items-center justify-end gap-3">
          <Button
            variant="ghost"
            onClick={() => setLocation('/yonetici/dashboard')}
            disabled={submitting}
          >
            İptal
          </Button>
          <Button
            onClick={handleCreate}
            loading={submitting}
            disabled={!selectedId}
          >
            Sınavı Oluştur
          </Button>
        </div>
      </main>
    </div>
  );
}
